/**
 * Display rows for dynamic Application Form custom questions (TT-65).
 * Pure derivation (tested): maps the config's enabled custom questions +
 * the stored answers (`customQuestionAnswers`, tolerant of the legacy
 * string encoding) to render-ready rows. Answer lookup mirrors the applicant
 * form exactly (form-gates validateCustomQuestionAnswers): dropdown fields
 * key as `${questionId}::${dropdownId}` with a plain-id fallback when the
 * dropdown id equals the question id; multi-selects join on '|'.
 */
import type {
	CustomQuestion,
	FormConfigV1,
} from '#app/utils/application-form/config'
import {
	enabledCustomQuestions,
	parseCustomQuestionAnswers,
} from '#app/utils/application-form/form-gates'
import { yesNoLabel } from './display'

const MULTI_SEP = '|'

export interface CustomQuestionRow {
	/** Stable render key: question id, or `${questionId}::${dropdownId}`. */
	key: string
	prompt: string
	kind: 'yes_no' | 'yes_no_explanation' | 'dropdown'
	/** Display-ready answer ('-' when unanswered; multi-select joined ', '). */
	answer: string
	/** Only for yes_no_explanation answered 'yes' (missing text renders '-'). */
	explanation: string | null
}

function dropdownAnswer(value: string, multiple: boolean): string {
	const trimmed = value.trim()
	if (!trimmed) return '-'
	if (!multiple) return trimmed
	const parts = trimmed
		.split(MULTI_SEP)
		.map((part) => part.trim())
		.filter(Boolean)
	return parts.length > 0 ? parts.join(', ') : '-'
}

function rowsForQuestion(
	question: CustomQuestion,
	answers: Record<string, { value: string; explanation?: string }>,
): CustomQuestionRow[] {
	if (question.type === 'dropdown' && question.dropdowns?.length) {
		return question.dropdowns.map((dropdown) => {
			const fieldId = `${question.id}::${dropdown.id}`
			const value =
				answers[fieldId]?.value ??
				(dropdown.id === question.id ? answers[question.id]?.value : '') ??
				''
			return {
				key: fieldId,
				// A single dropdown IS the question; several get the title suffix.
				prompt:
					question.dropdowns && question.dropdowns.length > 1
						? `${question.prompt} — ${dropdown.title}`
						: question.prompt,
				kind: 'dropdown' as const,
				answer: dropdownAnswer(value, dropdown.selection === 'multiple'),
				explanation: null,
			}
		})
	}
	const stored = answers[question.id]
	if (question.type === 'dropdown') {
		// Legacy shape: options directly on the question, single-select.
		return [
			{
				key: question.id,
				prompt: question.prompt,
				kind: 'dropdown',
				answer: dropdownAnswer(stored?.value ?? '', false),
				explanation: null,
			},
		]
	}
	const answer = yesNoLabel(stored?.value || undefined)
	return [
		{
			key: question.id,
			prompt: question.prompt,
			kind: question.type,
			answer,
			explanation:
				question.type === 'yes_no_explanation' && stored?.value === 'yes'
					? stored.explanation?.trim() || '-'
					: null,
		},
	]
}

/**
 * Rows for every ENABLED custom question of a block — one per question
 * (dropdown questions: one per dropdown field). Unanswered questions render
 * '-' rather than disappearing: the application completed under this frozen
 * config, so a missing answer is signal. [] when the block is off or has no
 * custom questions.
 */
export function deriveCustomQuestionRows(
	config: FormConfigV1,
	block: 'rental_questions' | 'employment_questions',
	rawAnswers: unknown,
): CustomQuestionRow[] {
	const questions = enabledCustomQuestions(config, block)
	if (questions.length === 0) return []
	const answers = parseCustomQuestionAnswers(rawAnswers)
	return questions.flatMap((question) => rowsForQuestion(question, answers))
}
